import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  confirmTossPayment, 
  selectWithcookieData,
  selectUser,
  fetchWithcookieData,
  selectPaymentConfirmation
} from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import { adaptPaymentSuccessProps } from './PaymentSuccess.adapter';
import { PaymentSuccessProps, PaymentInfo } from './PaymentSuccess.types';
import './PaymentSuccess.css';

const PaymentSuccessComponent: React.FC<PaymentSuccessProps> = (originalProps) => {
  // 어댑터를 통해 속성 정규화
  const props = adaptPaymentSuccessProps(originalProps);
  const { t } = useTranslation();
  
  // 컴포넌트 속성 추출
  const {
    className,
    style,
    componentProps
  } = props;

  // 컴포넌트 속성에서 필요한 값들 추출
  const {
    title = t("결제가 완료되었습니다"),
    successMessage = t("결제가 성공적으로 처리되었습니다."),
    orderHistoryButtonText = t("주문 내역 확인"),
    continueShoppingButtonText = t("쇼핑 계속하기"),
    showOrderDetails = true,
    redirectToHome = "/",
    redirectToOrderHistory = "/mypage/orderlist",
    loadingText = t("결제 정보를 처리 중입니다..."),
    waitingMessage = t("잠시만 기다려주세요.")
  } = componentProps || {};



  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const withcookieData = useSelector(selectWithcookieData);
  const user = useSelector(selectUser);
  const paymentConfirmation = useSelector(selectPaymentConfirmation);
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // 결제 요청 상태 관리를 위한 ref와 상태
  const requestIdRef = useRef<string | null>(null); // 현재 진행 중인 결제 요청 ID
  const [apiCallCount, setApiCallCount] = useState(0);
  const [responseResults, setResponseResults] = useState<{success: boolean, data: any}[]>([]);
  const [finalResult, setFinalResult] = useState<{success: boolean, data: any} | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // 결제 상태 메시지 조회 함수
  const getStatusMessage = useCallback((status: string) => {
    const statusMap: Record<string, string> = {
      'PENDING': t('결제 대기중'),
      'PROCESSING': t('처리중'),
      'PAID': t('결제완료'),
      'COMPLETED': t('주문완료'),
      'FAILED': t('실패'),
      'CANCELLED': t('취소됨'),
      'PREPARING': t('배송 준비중')
    };
    return statusMap[status] || status;
  }, [t]);

  // 결제 방법 한글명 변환
  const getPaymentMethodName = useCallback((method: string) => {
    const methodMap: Record<string, string> = {
      'card': t('신용카드'),
      'virtualAccount': t('가상계좌'),
      'transfer': t('계좌이체'),
      'phone': t('휴대폰결제'),
      'cultureland': t('문화상품권'),
      'smartcultureland': t('스마트문상'),
      'booknlife': t('도서문화상품권'),
      'happymoney': t('해피머니'),
      'CARD': t('신용카드'),
      'SEND': t('계좌이체'),
      'NONE': t('가상계좌')
    };
    return methodMap[method] || method;
  }, [t]);

  // 날짜 포맷팅 함수
  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }, []);

  // 컴포넌트 마운트 시 회사 정보 로드 (단 한 번만)
  useEffect(() => {
    // 이미 withcookieData가 있는 경우 불필요한 호출 방지
    if (!withcookieData) {
      dispatch(fetchWithcookieData() as any);
    }
  }, [dispatch, withcookieData]);

  // 응답 결과가 변경될 때 최종 결과 처리
  useEffect(() => {
    if (responseResults.length === apiCallCount && apiCallCount > 0 && isProcessing) {
      
      // 최소한 하나의 성공 응답이 있는지 확인
      const hasSuccess = responseResults.some(result => result.success);
      
      if (hasSuccess) {
        // 성공한 응답 중 첫 번째 데이터를 최종 결과로 사용
        const successResult = responseResults.find(result => result.success);
        setFinalResult({
          success: true,
          data: successResult?.data
        });
        
        // 성공한 응답의 데이터로 paymentInfo 설정
        const searchParams = new URLSearchParams(location.search);
        const orderId = searchParams.get('orderId') || '';
        const amount = searchParams.get('amount') || '0';
        const paymentType = searchParams.get('paymentType') || 'CARD';
        
        const checkoutInfo = successResult?.data?.checkout || {};
        setPaymentInfo({
          orderId: orderId,
          orderName: checkoutInfo.toss_respond?.orderName || t('주문 상품'),
          totalAmount: checkoutInfo.total_price || amount,
          method: checkoutInfo.payment_method || paymentType,
          status: checkoutInfo.status || 'PAID',
          paymentInfo: checkoutInfo.toss_respond || {},
          createdAt: checkoutInfo.createdAt || new Date().toISOString()
        });
      } else {
        // 모든 응답이 실패인 경우
        setFinalResult({
          success: false,
          data: responseResults[responseResults.length - 1]?.data || null
        });
      }
      
      // 처리 완료로 상태 변경
      setIsProcessing(false);
     
    }
  }, [responseResults, apiCallCount, location.search, t, isProcessing]);

  useEffect(() => {
    // 에디터 모드에서는 API 호출 방지
    if (props.isEditor || finalResult) {
     
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const paymentKey = searchParams.get('paymentKey');
    const orderId = searchParams.get('orderId');
    const amount = searchParams.get('amount');
    const paymentType = searchParams.get('paymentType') || 'CARD'; // 기본값 설정



    // 필수 파라미터 확인
    if (!paymentKey || !orderId || !amount) {
    
      return;
    }

    // 이미 처리 중이거나 결과가 있는 경우 중복 요청 방지
    if (isProcessing || requestIdRef.current) {
     
      return;
    }

    // Redux thunk 액션을 사용하여 결제 확인 요청
    const processPayment = async () => {
      try {
        // 첫 번째 요청일 경우 고유 요청 ID 생성
        const requestId = `payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        requestIdRef.current = requestId;
        setIsProcessing(true);
        setResponseResults([]);
        setApiCallCount(1); // 우선 1개 요청으로 시작
        
    
        
        // 회사 정보와 사용자 정보 확인
        let companyId = withcookieData?.company?.id || withcookieData?.skin?.companyId;
        const userId = user?.id;
        const skinId = withcookieData?.skin?.id;
        
        // withcookieData가 없는 경우에만 한 번 더 로드 시도
        if (!companyId) {
          if (retryCount === 0) {
           
            await dispatch(fetchWithcookieData() as any);
            setRetryCount(1);
            companyId = withcookieData?.company?.id || withcookieData?.skin?.companyId;
          }
          
          // 그래도 없으면 에러
          if (!companyId) {
            console.error(t("[결제 확인] 회사 ID를 찾을 수 없음:"), withcookieData);
            throw new Error(t("회사 정보를 불러올 수 없습니다. 페이지를 새로고침하거나 나중에 다시 시도해주세요."));
          }
        }

        // 주문 ID에서 checkout_id 추출 (예: "ORDER_123" -> 123)
        let checkoutId = null;
        if (orderId && orderId.startsWith('ORDER_')) {
          checkoutId = parseInt(orderId.replace('ORDER_', ''), 10);
        }

        if (!checkoutId) {
          console.error(t("[결제 확인] 올바른 checkout_id를 추출할 수 없습니다:"), orderId);
          throw new Error(t("주문 정보가 올바르지 않습니다. 다시 시도해주세요."));
        }

        // Redux 액션을 통해 결제 확인 요청
        try {
         
          const result = await dispatch(confirmTossPayment({
            checkout_id: checkoutId,
            user_id: userId,
            company_id: companyId,
            skin_id: skinId,
            toss_request: {
              paymentKey,
              orderId,
              paymentType,
              amount: Number(amount)
            }
          }) as any).unwrap();
          
         
          
          // 성공 응답 추가
          const isSuccess = result && (
            (result.statusCode >= 200 && result.statusCode < 400) || 
            result.success
          );
          
          setResponseResults([{ 
            success: isSuccess, 
            data: result 
          }]);
          
         
          
        } catch (apiError: any) {
          console.error(`[PaymentSuccess] API 요청 실패:`, apiError);
          
          // 403 에러 중에서 "이미 결제 요청이 완료된 주문입니다" 메시지인 경우 성공으로 처리
          const errorStatusCode = apiError?.statusCode || apiError?.response?.status;
          const errorMessage = apiError?.message || apiError?.response?.data?.message || '';
          
          const isAlreadyCompletedError = errorStatusCode === 403 && 
            (errorMessage.includes('이미 결제 요청이 완료된 주문입니다') ||
             errorMessage.includes('already completed') ||
             errorMessage.includes('이미 결제') ||
             errorMessage.includes('완료된 주문'));
          
          if (isAlreadyCompletedError) {
           
            
            // 이미 완료된 결제인 경우 성공으로 처리하고 기본 정보로 응답 생성
            const successData = {
              success: true,
              statusCode: 200,
              message: '결제가 이미 완료되었습니다',
              checkout: {
                id: checkoutId,
                status: 'PAID',
                total_price: Number(amount),
                payment_method: paymentType,
                createdAt: new Date().toISOString(),
                toss_respond: {
                  orderId: orderId,
                  orderName: t('주문 상품'),
                  paymentKey: paymentKey,
                  amount: Number(amount),
                  method: paymentType
                }
              }
            };
            
            setResponseResults([{ 
              success: true, 
              data: successData 
            }]);
          } else {
            // 다른 종류의 실패 응답 추가
            setResponseResults([{ 
              success: false, 
              data: apiError 
            }]);
          }
        }
      } catch (err) {
        console.error(t('결제 처리 오류:'), err);
        
        // 예외 발생 시 응답 처리
        setResponseResults([{ 
          success: false, 
          data: { 
            message: (err as Error).message || t("알 수 없는 오류가 발생했습니다") 
          } 
        }]);
        
        setApiCallCount(1); // 1개 요청으로 설정
        setIsProcessing(false);
        requestIdRef.current = null;
      }
    };

    // 결제 정보가 모두 있으면 결제 확인 진행
    processPayment();
  }, [location.search, props.isEditor]); // 의존성을 최소화

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      // 컴포넌트가 언마운트될 때 요청 ID 정리
      requestIdRef.current = null;
    };
  }, []);

  const handleGoToOrderHistory = useCallback(() => {
    navigate(redirectToOrderHistory);
  }, [navigate, redirectToOrderHistory]);

  const handleGoToHome = useCallback(() => {
    navigate(redirectToHome);
  }, [navigate, redirectToHome]);

  // 에디터 모드에서 더미 데이터 사용
  const dummyPaymentInfo = useMemo(() => {
    return props.isEditor ? {
      orderId: 'ORDER_12345',
      orderName: t('에디터 모드 샘플 상품'),
      totalAmount: 42000,
      method: 'CARD',
      status: 'PAID',
      paymentInfo: {},
      createdAt: new Date().toISOString()
    } : null;
  }, [props.isEditor, t]);

  // 에디터 모드이거나 로딩 중일 때 표시할 컴포넌트
  if (props.isEditor && !dummyPaymentInfo) {
    return (
      <div className={className} style={style}>
        <div className="payment-result-container">
          <div className="payment-result-card loading">
            <div className="spinner"></div>
            <h2>{loadingText}</h2>
            <p>{waitingMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  // 로딩 상태 표시 (에디터 모드가 아닐 때)
  if (!props.isEditor && (isProcessing || !finalResult)) {
    return (
      <div className={className} style={style}>
        <div className="payment-result-container">
          <div className="payment-result-card loading">
            <div className="spinner"></div>
            <h2>{loadingText}</h2>
            <p>{waitingMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  // 성공 여부에 따라 화면 표시
  const isSuccess = props.isEditor || (finalResult && finalResult.success);

  // 성공 상태로 처리
  if (isSuccess) {
    // 에디터 모드이거나 실제 결제 성공한 경우
    const displayPaymentInfo = props.isEditor ? dummyPaymentInfo : paymentInfo;
    
    return (
      <div className={className} style={style}>
        <div className="payment-result-container">
          <div className="payment-result-card success">
            <div className="success-icon">✓</div>
            <h2>{title}</h2>
            
            {displayPaymentInfo && showOrderDetails ? (
              <div className="payment-info">
                <div className="info-row">
                  <span className="label">{t("주문번호")}:</span>
                  <span className="value">{displayPaymentInfo.orderId || ''}</span>
                </div>
                <div className="info-row">
                  <span className="label">{t("상품명")}:</span>
                  <span className="value">{t(displayPaymentInfo.orderName || '')}</span>
                </div>
                <div className="info-row">
                  <span className="label">{t("결제금액")}:</span>
                  <span className="value">
                    {typeof displayPaymentInfo.totalAmount === 'number' 
                      ? new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(displayPaymentInfo.totalAmount)
                      : new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(Number(displayPaymentInfo.totalAmount) || 0)
                    }
                  </span>
                </div>
                <div className="info-row">
                  <span className="label">{t("결제방법")}:</span>
                  <span className="value">{getPaymentMethodName(displayPaymentInfo.method || '')}</span>
                </div>
                <div className="info-row">
                  <span className="label">{t("결제상태")}:</span>
                  <span className="value">{getStatusMessage(displayPaymentInfo.status || '')}</span>
                </div>
                <div className="info-row">
                  <span className="label">{t("결제일시")}:</span>
                  <span className="value">{formatDate(displayPaymentInfo.createdAt || '')}</span>
                </div>
              </div>
            ) : (
              <p>{successMessage}</p>
            )}
            
            <div className="payment-action-buttons">
              <button onClick={handleGoToOrderHistory} className="view-orders-button">
                {orderHistoryButtonText || '주문 내역 확인'}
              </button>
              <button onClick={handleGoToHome} className="continue-shopping-button">
                {continueShoppingButtonText || '쇼핑 계속하기'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // 에러 상태 표시
  else {
    // finalResult에서 에러 메시지 추출
    const errorMessage = finalResult?.data 
      ? (typeof finalResult.data === 'object' && finalResult.data !== null
        ? ((finalResult.data as any).message || JSON.stringify(finalResult.data)) 
        : String(finalResult.data))
      : t("알 수 없는 오류가 발생했습니다.");

    return (
      <div className={className} style={style}>
        <div className="payment-result-container">
          <div className="payment-result-card error">
            <div className="error-icon">❌</div>
            <h2>{t("결제 처리 중 오류가 발생했습니다")}</h2>
            <p>{errorMessage}</p>
            <div className="payment-action-buttons">
              <button onClick={handleGoToHome} className="go-home-button">
                {t("홈으로 돌아가기")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default PaymentSuccessComponent; 